import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, XCircle, Envelope } from "@phosphor-icons/react";
import { http } from "@/lib/api";

/**
 * Landing page for the email-verification link fired by
 * `send_verification_email()`. Hits `GET /api/auth/verify-email?token=X`
 * on mount, shows success / expired / already-used states, and pushes
 * the user to `/app` (if logged in) or `/login` (if not) after 2s.
 */
export default function VerifyEmail() {
  const [state, setState] = useState({ status: "verifying", message: null });
  const [params] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState({ status: "error", message: "Missing verification token." });
      return;
    }
    http.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then((r) => {
        if (r.data?.already_verified) {
          setState({ status: "already", message: "Your email was already confirmed. Redirecting…" });
        } else {
          setState({ status: "ok", message: "Email confirmed. Redirecting…" });
        }
        setTimeout(() => nav("/app"), 1800);
      })
      .catch((e) => {
        setState({
          status: "error",
          message: e?.response?.data?.detail || "Verification failed. Request a new link from your account.",
        });
      });
  }, [params, nav]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-6" data-testid="verify-email-page">
      <div className="max-w-md w-full bg-[#121212] border border-zinc-800 p-8 text-center">
        <div className="flex justify-center mb-4">
          {state.status === "verifying" && <Envelope size={48} weight="duotone" className="text-[#39FF14] animate-pulse" />}
          {(state.status === "ok" || state.status === "already") && <CheckCircle size={48} weight="fill" className="text-[#39FF14]" />}
          {state.status === "error" && <XCircle size={48} weight="fill" className="text-[#FF3B30]" />}
        </div>
        <h1 className="heading text-2xl font-black uppercase mb-2">
          {state.status === "verifying" ? "Confirming…"
            : state.status === "ok" ? "Confirmed"
            : state.status === "already" ? "Already Confirmed"
            : "Verification Failed"}
        </h1>
        <p className="text-sm text-zinc-400 leading-relaxed">
          {state.message || "Working on it…"}
        </p>
        {state.status === "error" && (
          <div className="mt-6 flex flex-col gap-2">
            <button
              onClick={() => nav("/app")}
              data-testid="verify-goto-app"
              className="bg-[#39FF14] hover:bg-[#2ee30d] text-black font-black uppercase tracking-widest text-xs py-2.5"
            >
              Go to my account
            </button>
            <button
              onClick={() => http.post("/auth/resend-verification").then((r) => {
                toast.success(r.data?.message || "Verification email sent.");
              }).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't resend"))}
              className="text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-white"
              data-testid="verify-resend"
            >
              Resend verification email
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
